import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Straighten as StraightenIcon,
  Percent as PercentIcon
} from '@mui/icons-material';
import type { ElevationPoint } from '../../types';
import {
  detectClimbs,
  formatClimbInfo,
  getClimbCategoryColor,
  DEFAULT_CLIMB_CONFIG
} from '../../utils/climbDetection';
import type {
  ClimbSegment,
  ClimbDetectionConfig
} from '../../utils/climbDetection';
import { ClimbConfigModal } from './ClimbConfigModal';
import { ClimbElevationChart } from './ClimbElevationChart';
import './ClimbAnalysis.css';

interface ClimbAnalysisProps {
  elevationData: ElevationPoint[];
  onClimbClick?: (climbIndex: number | null) => void; // New prop for clicking climbs
  activeClimb?: number | null;
  variant?: 'default' | 'detail';
  config?: ClimbDetectionConfig;
  onConfigChange?: (config: ClimbDetectionConfig) => void;
  climbs?: ClimbSegment[]; // Optional pre-calculated climbs
}

interface ClimbCardProps {
  climb: ClimbSegment;
  index: number;
  isActive: boolean;
  onHover?: (index: number | null) => void; // Made optional since we no longer use it
  onClick?: (index: number) => void; // New prop for clicking
  elevationData: ElevationPoint[]; // Add elevation data for the chart
}

const ClimbCard: React.FC<ClimbCardProps> = ({ climb, index, isActive, onClick, elevationData }) => {
  const climbInfo = formatClimbInfo(climb);
  const categoryColor = getClimbCategoryColor(climb.category);
  
  return (
    <Box
      onClick={() => onClick?.(index)}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: { xs: 1.5, sm: 2 },
        backgroundColor: isActive ? `${categoryColor}20` : 'transparent',
        borderLeft: isActive ? `4px solid ${categoryColor}` : '4px solid transparent',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: `${categoryColor}15`,
          borderLeftColor: categoryColor,
          transform: 'translateX(2px)',
        }
      }}
    >
      {/* Left side - Title and location */}
      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '0.85rem', sm: '0.9rem' }
            }}
          >
            {climbInfo.title}
          </Typography>
          {climb.category && (
            <Chip
              label={climb.category}
              size="small"
              sx={{
                backgroundColor: categoryColor,
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                height: { xs: '18px', sm: '20px' }
              }}
            />
          )}
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            fontSize: { xs: '0.7rem', sm: '0.75rem' }
          }}
        >
          📍 {climbInfo.distance}
        </Typography>
      </Box>
      
      {/* Center - Elevation Chart */}
      <Box sx={{ mx: { xs: 1, sm: 2 } }}>
        <ClimbElevationChart
          climb={climb}
          elevationData={elevationData}
          width={80}
          height={40}
        />
      </Box>
      
      {/* Right side - Stats */}
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 1, sm: 2 }, 
        alignItems: 'center'
      }}>
        <Box textAlign="center" sx={{ minWidth: { xs: '35px', sm: '40px' } }}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            {climbInfo.length}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
            length
          </Typography>
        </Box>
        <Box textAlign="center" sx={{ minWidth: { xs: '35px', sm: '40px' } }}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            {climbInfo.elevationGain}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
            gain
          </Typography>
        </Box>
        <Box textAlign="center" sx={{ minWidth: { xs: '35px', sm: '40px' } }}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            {climbInfo.averageGradient}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
            avg
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const ClimbAnalysis: React.FC<ClimbAnalysisProps> = ({
  elevationData,
  onClimbClick,
  activeClimb,
  variant = 'default',
  config = DEFAULT_CLIMB_CONFIG,
  onConfigChange,
  climbs: passedClimbs
}) => {
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  
  const climbs = useMemo(() => {
    if (!elevationData || elevationData.length === 0) {
      return [];
    }

    // Use passed climbs if available, otherwise detect them
    return passedClimbs || detectClimbs(elevationData, config);
  }, [elevationData, config, passedClimbs]);

  const handleClimbClick = (climbIndex: number) => {
    // Toggle behavior: if same climb is clicked, deselect it
    const newIndex = activeClimb === climbIndex ? null : climbIndex;
    onClimbClick?.(newIndex);
  };

  if (!elevationData || elevationData.length === 0) {
    return (
      <Box 
        className="climb-analysis climb-analysis--empty" 
        sx={{ 
          p: 2,
          backgroundColor: 'background.paper',
          color: 'text.primary'
        }}
      >
        <Typography color="text.secondary" textAlign="center">
          No elevation data available for climb analysis
        </Typography>
      </Box>
    );
  }

  if (climbs.length === 0) {
    return (
      <Box 
        className="climb-analysis climb-analysis--no-climbs" 
        sx={{ 
          p: 2,
          backgroundColor: 'background.paper',
          color: 'text.primary'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box textAlign="center" flex={1}>
            <Typography color="text.secondary" textAlign="center">
              No significant climbs detected on this route
            </Typography>
            <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mt: 1 }}>
              (Current thresholds: {config.minElevationGain}m gain, {config.minDistance}m distance, {config.minAverageGradient}% gradient)
            </Typography>
          </Box>
          {onConfigChange && (
            <Tooltip title="Adjust climb detection settings">
              <IconButton 
                onClick={() => setConfigModalOpen(true)}
                sx={{ color: 'text.secondary' }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {onConfigChange && (
          <ClimbConfigModal
            open={configModalOpen}
            onClose={() => setConfigModalOpen(false)}
            config={config}
            onConfigChange={onConfigChange}
          />
        )}
      </Box>
    );
  }

  return (
    <Box 
      className={`climb-analysis climb-analysis--${variant}`} 
      sx={{ 
        pb: 2,
        backgroundColor: 'background.paper',
        color: 'text.primary'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Detected Climbs ({climbs.length})
        </Typography>
        <Box display="flex" gap={0.5}>
          <Tooltip title="How climbs are detected and classified">
            <IconButton 
              size="small" 
              onClick={() => setInfoModalOpen(true)}
              sx={{ color: 'text.secondary' }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          {onConfigChange && (
            <Tooltip title="Configure climb detection settings">
              <IconButton 
                size="small" 
                onClick={() => setConfigModalOpen(true)}
                sx={{ color: 'text.secondary' }}
              >
                <SettingsIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      
      <Box 
        className="climb-cards-container"
        sx={{
          '& > *:not(:last-child)': {
            borderBottom: '1px solid',
            borderBottomColor: 'divider'
          }
        }}
      >
        {climbs.map((climb, index) => (
          <ClimbCard
            key={climb.id}
            climb={climb}
            index={index}
            isActive={activeClimb === index}
            onClick={handleClimbClick}
            elevationData={elevationData}
          />
        ))}
      </Box>

      {onConfigChange && (
        <ClimbConfigModal
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          config={config}
          onConfigChange={onConfigChange}
        />
      )}

      {/* Climb Detection Info Modal */}
      <Dialog 
        open={infoModalOpen} 
        onClose={() => setInfoModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxWidth: '800px' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            Climb Detection & Classification
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mt: 0, mb: 1, color: 'primary.main', fontSize: '1.1rem' }}>
            How Climbs are Detected
          </Typography>
          <List dense sx={{ py: 0 }}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <TrendingUpIcon color="primary" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Elevation Analysis"
                secondary="The system continuously analyzes elevation changes along your route to identify sustained uphill sections."
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <StraightenIcon color="primary" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Minimum Requirements"
                secondary={`A climb must have at least ${config.minElevationGain}m elevation gain over a minimum distance of ${config.minDistance}m to be detected.`}
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PercentIcon color="primary" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Gradient Threshold"
                secondary={`The average gradient must be at least ${config.minAverageGradient}% to qualify as a climb worth categorizing.`}
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main', fontSize: '1.1rem' }}>
            Climb Classification System
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.85rem', mb: 1 }}>
            Based on traditional cycling categorization using difficulty points calculated from elevation gain, length, and average gradient:
          </Typography>
          
          <List dense sx={{ py: 0 }}>
            <ListItem sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 50 }}>
                <Chip label="HC" size="small" sx={{ backgroundColor: '#6a1b9a', color: 'white', fontWeight: 'bold', minWidth: 32, fontSize: '0.75rem' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Hors Catégorie (Beyond Category)"
                secondary="The most challenging climbs with 1200+ difficulty points. Legendary mountain passes and epic ascents."
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 50 }}>
                <Chip label="1" size="small" sx={{ backgroundColor: '#d32f2f', color: 'white', fontWeight: 'bold', minWidth: 32, fontSize: '0.75rem' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Category 1"
                secondary="Very difficult climbs (600-1199 points). Serious mountain climbs requiring significant effort."
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 50 }}>
                <Chip label="2" size="small" sx={{ backgroundColor: '#f57c00', color: 'white', fontWeight: 'bold', minWidth: 32, fontSize: '0.75rem' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Category 2"
                secondary="Difficult climbs (300-599 points). Substantial ascents that will test your climbing ability."
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 50 }}>
                <Chip label="3" size="small" sx={{ backgroundColor: '#fbc02d', color: 'white', fontWeight: 'bold', minWidth: 32, fontSize: '0.75rem' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Category 3"
                secondary="Moderate climbs (150-299 points). Noticeable climbs requiring sustained effort."
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.4 }}>
              <ListItemIcon sx={{ minWidth: 50 }}>
                <Chip label="4" size="small" sx={{ backgroundColor: '#388e3c', color: 'white', fontWeight: 'bold', minWidth: 32, fontSize: '0.75rem' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Category 4"
                secondary="Easier climbs (45-149 points). Gentle sustained climbs, good for building endurance."
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main', fontSize: '1.1rem' }}>
            Elevation Chart Colors
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.85rem', mb: 1 }}>
            The small elevation charts show gradient-based color coding:
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={0.8} sx={{ mt: 1, mb: 2 }}>
            <Chip label="0-3% Easy" size="small" sx={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.75rem', height: 24 }} />
            <Chip label="3-6% Moderate" size="small" sx={{ backgroundColor: '#8bc34a', color: 'white', fontSize: '0.75rem', height: 24 }} />
            <Chip label="6-9% Challenging" size="small" sx={{ backgroundColor: '#ffeb3b', color: 'black', fontSize: '0.75rem', height: 24 }} />
            <Chip label="9-12% Hard" size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.75rem', height: 24 }} />
            <Chip label="12-15% Very Hard" size="small" sx={{ backgroundColor: '#f44336', color: 'white', fontSize: '0.75rem', height: 24 }} />
            <Chip label="15%+ Extreme" size="small" sx={{ backgroundColor: '#9c27b0', color: 'white', fontSize: '0.75rem', height: 24 }} />
          </Box>

          {onConfigChange && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                💡 <strong>Tip:</strong> You can adjust the detection parameters using the settings button (⚙️) next to "Detected Climbs" to fine-tune which climbs are detected based on your preferences.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClimbAnalysis;
